import React, { useEffect, useState } from "react";
import AdminWrapper from "../../../components/AdminWrapper";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { GetAdmin } from "../../../redux/reducer/GetAdminDetail";
import { AcceptLandlord, RejectLandlord } from "../../../redux/reducer/AcceptRejectAdmin";
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
  Tab,
  Tabs,
  Skeleton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Search,
  Visibility,
  CheckCircle,
  Cancel,
  Close,
  Download,
  Refresh,
  Email,
  Phone,
  LocationOn,
  Badge,
  CalendarMonth,
  ThumbUp,
  ThumbDown,
  HourglassEmpty,
} from "@mui/icons-material";
import Swal from "sweetalert2";

const LandlordManagement = () => {
  const dispatch = useDispatch();
  const open = useSelector((state) => state.sidebar.open);
  const { loading, GetAdminDetail } = useSelector((state) => state.getAdminDetail);
  const stats = GetAdminDetail;

  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedLandlord, setSelectedLandlord] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    dispatch(GetAdmin());
  }, [dispatch]);

  const landlords = stats?.landlord_detail || [];

  // Filter landlords
  const filteredLandlords = landlords.filter((landlord) => {
    const matchesSearch =
      (landlord.name?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (landlord.email?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (landlord.contactNumber?.toLowerCase() || "").includes(searchQuery.toLowerCase());

    let matchesStatus = true;
    if (filterStatus === "active") {
      matchesStatus = landlord.is_active === true;
    } else if (filterStatus === "pending") {
      matchesStatus = landlord.is_register === true && landlord.is_active === false;
    } else if (filterStatus === "inactive") {
      matchesStatus = landlord.is_active === false;
    }

    // Tab filtering
    if (activeTab === 1) {
      // Pending tab
      matchesStatus = landlord.is_register === true && landlord.is_active === false;
    } else if (activeTab === 2) {
      // Active tab
      matchesStatus = landlord.is_active === true;
    }

    return matchesSearch && matchesStatus;
  });

  const handleAccept = async (landlord) => {
    const result = await Swal.fire({
      title: "Accept Landlord?",
      text: `Are you sure you want to approve ${landlord.name || landlord.email}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#22c55e",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Approve",
    });

    if (result.isConfirmed) {
      setActionLoading(landlord._id);
      dispatch(AcceptLandlord(landlord._id))
        .unwrap()
        .then(() => {
          toast.success("Landlord approved successfully!", { autoClose: 2000 });
          dispatch(GetAdmin());
        })
        .catch((error) => {
          toast.error("Failed to approve landlord");
        })
        .finally(() => {
          setActionLoading(null);
        });
    }
  };

  const handleReject = async (landlord) => {
    const result = await Swal.fire({
      title: "Reject Landlord?",
      text: `Are you sure you want to reject ${landlord.name || landlord.email}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Reject",
    });

    if (result.isConfirmed) {
      setActionLoading(landlord._id);
      dispatch(RejectLandlord(landlord._id))
        .unwrap()
        .then(() => {
          toast.success("Landlord rejected successfully!", { autoClose: 2000 });
          dispatch(GetAdmin());
        })
        .catch((error) => {
          toast.error("Failed to reject landlord");
        })
        .finally(() => {
          setActionLoading(null);
        });
    }
  };

  const handleViewDetails = (landlord) => {
    setSelectedLandlord(landlord);
    setDetailsOpen(true);
  };

  const pendingCount = landlords.filter((l) => l.is_register === true && l.is_active === false).length;
  const activeCount = landlords.filter((l) => l.is_active === true).length;

  const columns = [
    {
      field: "index",
      headerName: "#",
      width: 60,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: "name",
      headerName: "Landlord",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
          <Avatar
            src={params.row.profileImage}
            sx={{ width: 42, height: 42, bgcolor: "#22c55e20", color: "#22c55e" }}
          >
            {(params.row.name || "L").charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {params.row.name || "N/A"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "contactNumber",
      headerName: "Contact",
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Phone sx={{ fontSize: 16, color: "#9ca3af" }} />
          <Typography variant="body2">{params.value || "N/A"}</Typography>
        </Box>
      ),
    },
    {
      field: "cnic",
      headerName: "CNIC",
      width: 150,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => {
        const isPending = params.row.is_register === true && params.row.is_active === false;
        const isActive = params.row.is_active === true;

        return (
          <Chip
            icon={
              isPending ? (
                <HourglassEmpty sx={{ fontSize: 16 }} />
              ) : isActive ? (
                <CheckCircle sx={{ fontSize: 16 }} />
              ) : (
                <Cancel sx={{ fontSize: 16 }} />
              )
            }
            label={isPending ? "Pending" : isActive ? "Active" : "Inactive"}
            size="small"
            sx={{
              bgcolor: isPending ? "#fef3c7" : isActive ? "#dcfce7" : "#fee2e2",
              color: isPending ? "#92400e" : isActive ? "#166534" : "#991b1b",
              fontWeight: 500,
              "& .MuiChip-icon": { color: "inherit" },
            }}
          />
        );
      },
    },
    {
      field: "date",
      headerName: "Registered",
      width: 120,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : "N/A",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      sortable: false,
      renderCell: (params) => {
        const isPending = params.row.is_register === true && params.row.is_active === false;
        const isLoading = actionLoading === params.row._id;

        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="View Details">
              <IconButton size="small" onClick={() => handleViewDetails(params.row)}>
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
            {isPending && (
              <>
                <Tooltip title="Approve">
                  <IconButton
                    size="small"
                    onClick={() => handleAccept(params.row)}
                    disabled={isLoading}
                    sx={{
                      color: "#22c55e",
                      "&:hover": { bgcolor: "#22c55e15" },
                    }}
                  >
                    {isLoading ? <CircularProgress size={18} /> : <ThumbUp fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reject">
                  <IconButton
                    size="small"
                    onClick={() => handleReject(params.row)}
                    disabled={isLoading}
                    sx={{
                      color: "#ef4444",
                      "&:hover": { bgcolor: "#ef444415" },
                    }}
                  >
                    <ThumbDown fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        );
      },
    },
  ];

  // Loading Skeleton
  const LoadingSkeleton = () => (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Skeleton variant="text" width={220} height={40} />
          <Skeleton variant="text" width={180} height={20} />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Skeleton variant="rounded" width={100} height={36} />
          <Skeleton variant="rounded" width={100} height={36} />
        </Box>
      </Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={12} sm={4} key={i}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Skeleton variant="text" width={60} height={45} sx={{ mx: "auto" }} />
                <Skeleton variant="text" width={100} height={20} sx={{ mx: "auto" }} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Skeleton variant="rounded" width="100%" height={48} sx={{ mb: 2, borderRadius: 2 }} />
      <Skeleton variant="rounded" width="100%" height={56} sx={{ mb: 2, borderRadius: 2 }} />
      <Skeleton variant="rounded" width="100%" height={400} sx={{ borderRadius: 2 }} />
    </>
  );

  return (
    <>
      <AdminWrapper />
      <Box
        sx={{
          ml: { xs: 0, md: open ? "260px" : "65px" },
          transition: "margin 0.3s ease",
          minHeight: "100vh",
          bgcolor: "#f8fafc",
          p: { xs: 2, md: 3 },
          pt: { xs: 10, md: 11 },
        }}
      >
        {loading ? <LoadingSkeleton /> : (
        <>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#1e293b" }}>
              Landlord Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review and manage landlord registrations
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => dispatch(GetAdmin())}
              sx={{ borderColor: "#e5e7eb", color: "#64748b" }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              sx={{ bgcolor: "#e94560", "&:hover": { bgcolor: "#d13652" } }}
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4}>
            <Card sx={{ borderRadius: 2, border: "1px solid #e5e7eb" }}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#6366f1" }}>
                  {landlords.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Landlords
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Card
              sx={{
                borderRadius: 2,
                border: "1px solid #fcd34d",
                bgcolor: pendingCount > 0 ? "#fffbeb" : "white",
              }}
            >
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#d97706" }}>
                  {pendingCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Approval
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ borderRadius: 2, border: "1px solid #86efac" }}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#22c55e" }}>
                  {activeCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Landlords
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper sx={{ mb: 2, borderRadius: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              borderBottom: "1px solid #e5e7eb",
              "& .MuiTab-root": { textTransform: "none", fontWeight: 500 },
            }}
          >
            <Tab label={`All (${landlords.length})`} />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Pending
                  {pendingCount > 0 && (
                    <Chip
                      label={pendingCount}
                      size="small"
                      sx={{ bgcolor: "#fbbf24", color: "#78350f", height: 20, fontSize: 12 }}
                    />
                  )}
                </Box>
              }
            />
            <Tab label={`Active (${activeCount})`} />
          </Tabs>
        </Paper>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, email, or contact..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#9ca3af" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchQuery("");
                  setFilterStatus("all");
                }}
                sx={{ borderColor: "#e5e7eb" }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Data Table */}
        <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
          <DataGrid
            rows={filteredLandlords}
            columns={columns}
            getRowId={(row) => row._id || Math.random()}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: "#f8fafc",
                borderBottom: "1px solid #e5e7eb",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
                color: "#374151",
              },
              "& .MuiDataGrid-row:hover": {
                bgcolor: "#f8fafc",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #f3f4f6",
              },
            }}
          />
        </Paper>

        {/* Landlord Details Dialog */}
        <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Landlord Details
            </Typography>
            <IconButton onClick={() => setDetailsOpen(false)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedLandlord && (
              <Box>
                {/* Profile Header */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3 }}>
                  <Avatar
                    src={selectedLandlord.profileImage}
                    sx={{
                      width: 100,
                      height: 100,
                      bgcolor: "#22c55e20",
                      color: "#22c55e",
                      fontSize: 40,
                    }}
                  >
                    {(selectedLandlord.name || "L").charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {selectedLandlord.name || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {selectedLandlord.email}
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        selectedLandlord.is_register && !selectedLandlord.is_active
                          ? "Pending Approval"
                          : selectedLandlord.is_active
                          ? "Active"
                          : "Inactive"
                      }
                      sx={{
                        bgcolor:
                          selectedLandlord.is_register && !selectedLandlord.is_active
                            ? "#fef3c7"
                            : selectedLandlord.is_active
                            ? "#dcfce7"
                            : "#fee2e2",
                        color:
                          selectedLandlord.is_register && !selectedLandlord.is_active
                            ? "#92400e"
                            : selectedLandlord.is_active
                            ? "#166534"
                            : "#991b1b",
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Details Grid */}
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                      <Phone sx={{ color: "#6366f1" }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Contact Number
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedLandlord.contactNumber || "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                      <Badge sx={{ color: "#6366f1" }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          CNIC
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedLandlord.cnic || "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                      <Email sx={{ color: "#6366f1" }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Email Address
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedLandlord.email || "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                      <CalendarMonth sx={{ color: "#6366f1" }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Registration Date
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedLandlord.date
                            ? new Date(selectedLandlord.date).toLocaleDateString()
                            : "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  {selectedLandlord.address && (
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        <LocationOn sx={{ color: "#6366f1" }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Address
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {selectedLandlord.address}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                {/* CNIC Images */}
                {(selectedLandlord.cnicFront || selectedLandlord.cnicBack) && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      CNIC Images
                    </Typography>
                    <Grid container spacing={2}>
                      {selectedLandlord.cnicFront && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                            Front Side
                          </Typography>
                          <Box
                            component="img"
                            src={selectedLandlord.cnicFront}
                            alt="CNIC Front"
                            sx={{
                              width: "100%",
                              maxHeight: 200,
                              objectFit: "cover",
                              borderRadius: 2,
                              border: "1px solid #e5e7eb",
                            }}
                          />
                        </Grid>
                      )}
                      {selectedLandlord.cnicBack && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                            Back Side
                          </Typography>
                          <Box
                            component="img"
                            src={selectedLandlord.cnicBack}
                            alt="CNIC Back"
                            sx={{
                              width: "100%",
                              maxHeight: 200,
                              objectFit: "cover",
                              borderRadius: 2,
                              border: "1px solid #e5e7eb",
                            }}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            {selectedLandlord?.is_register && !selectedLandlord?.is_active && (
              <>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<ThumbDown />}
                  onClick={() => {
                    setDetailsOpen(false);
                    handleReject(selectedLandlord);
                  }}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  startIcon={<ThumbUp />}
                  onClick={() => {
                    setDetailsOpen(false);
                    handleAccept(selectedLandlord);
                  }}
                  sx={{ bgcolor: "#22c55e", "&:hover": { bgcolor: "#16a34a" } }}
                >
                  Approve
                </Button>
              </>
            )}
            <Button onClick={() => setDetailsOpen(false)} variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>
        </>
        )}
      </Box>
    </>
  );
};

export default LandlordManagement;
